/**
 * Bulk celebration period grouping — shared by chat-concierge and the
 * registered dashboard. One recommendation engine call; multiple
 * celebration rows attach to one order via celebration_ids on the brief.
 */

export type BulkCelebrationCadence =
  | "weekly"
  | "biweekly"
  | "monthly"
  | "quarterly";

export type DeliveryAnchor =
  | { kind: "fixed_day_of_period"; day_of_month: number }
  | { kind: "nth_weekday_of_period"; weekday: number; nth: 1 | -1 }
  | { kind: "first_celebration_in_period" }
  | { kind: "offset_from_period_start"; days: number };

export type BulkCelebrationProgramRow = {
  company_id: string;
  enabled_at: string | null;
  cadence: BulkCelebrationCadence;
  default_tier: "standard" | "premium" | "luxury";
  budget_cents: number | null;
  delivery_address_id: string | null;
  delivery_window: Record<string, unknown> | null;
  delivery_anchor: DeliveryAnchor | null;
  occasion_types: string[] | null;
  updated_at: string | null;
};

export const BULK_OCCASION_TYPES = [
  "birthday",
  "work_anniversary",
  "welcome",
  "farewell",
  "milestone",
] as const;

export type BulkRunStatus =
  | "draft"
  | "sent_for_approval"
  | "approved"
  | "ordered"
  | "skipped";

export type BulkPeriodCelebrationRow = {
  celebration_id: string;
  date: string;
  type: string;
  status: string;
  team_member_id: string;
  team_member_name: string | null;
  dietary_requirements: string[];
};

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** ISO week year + week number (Monday-based). */
function isoWeekKey(d: Date): { year: number; week: number } {
  const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((utc.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { year: utc.getUTCFullYear(), week };
}

export function bulkPeriodBounds(
  cadence: BulkCelebrationCadence,
  anchor: Date,
): { period_key: string; period_start: string; period_end: string; label: string } {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  const start = new Date(anchor);
  start.setHours(0, 0, 0, 0);

  if (cadence === "monthly") {
    const periodStart = new Date(y, m, 1);
    const periodEnd = new Date(y, m + 1, 0);
    const key = `${y}-${pad2(m + 1)}`;
    return {
      period_key: key,
      period_start: isoDate(periodStart),
      period_end: isoDate(periodEnd),
      label: periodStart.toLocaleString("en-AU", { month: "long", year: "numeric" }),
    };
  }

  if (cadence === "quarterly") {
    const q = Math.floor(m / 3);
    const periodStart = new Date(y, q * 3, 1);
    const periodEnd = new Date(y, q * 3 + 3, 0);
    const key = `${y}-Q${q + 1}`;
    return {
      period_key: key,
      period_start: isoDate(periodStart),
      period_end: isoDate(periodEnd),
      label: `Q${q + 1} ${y}`,
    };
  }

  if (cadence === "weekly") {
    const { year, week } = isoWeekKey(anchor);
    const day = start.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    const periodStart = new Date(start);
    periodStart.setDate(periodStart.getDate() + diff);
    const periodEnd = new Date(periodStart);
    periodEnd.setDate(periodEnd.getDate() + 6);
    const key = `${year}-W${pad2(week)}`;
    return {
      period_key: key,
      period_start: isoDate(periodStart),
      period_end: isoDate(periodEnd),
      label: `Week ${week}, ${year}`,
    };
  }

  // biweekly — anchor fortnight containing `anchor` (epoch-aligned pairs)
  const epoch = new Date(2020, 0, 6); // Monday
  epoch.setHours(0, 0, 0, 0);
  const ms = start.getTime() - epoch.getTime();
  const fortnightIndex = Math.floor(ms / (14 * 86400000));
  const periodStart = new Date(epoch.getTime() + fortnightIndex * 14 * 86400000);
  const periodEnd = new Date(periodStart);
  periodEnd.setDate(periodEnd.getDate() + 13);
  const key = `${periodStart.getFullYear()}-${pad2(periodStart.getMonth() + 1)}-${pad2(periodStart.getDate())}`;
  return {
    period_key: `fn-${key}`,
    period_start: isoDate(periodStart),
    period_end: isoDate(periodEnd),
    label: `${isoDate(periodStart)} – ${isoDate(periodEnd)}`,
  };
}

/** Dietaries every member in the bulk group must satisfy (intersection). */
export function mergeBulkDietaries(
  members: { dietary_requirements?: string[] | null }[],
): string[] {
  const lists = members
    .map((m) => (m.dietary_requirements ?? []).map((d) => d.trim().toLowerCase()).filter(Boolean))
    .filter((a) => a.length > 0);
  if (lists.length === 0) return [];
  let acc = new Set(lists[0]);
  for (let i = 1; i < lists.length; i++) {
    const next = new Set(lists[i]);
    acc = new Set([...acc].filter((t) => next.has(t)));
  }
  return [...acc];
}

export function isBulkProgramActive(program: BulkCelebrationProgramRow | null): boolean {
  return Boolean(program?.enabled_at);
}

/** Label passed to the recommendation engine as recipientName (period variation). */
export function bulkRecommendationRecipientLabel(
  periodLabel: string,
  headcount: number,
): string {
  return `${periodLabel} (${headcount} ${headcount === 1 ? "person" : "people"})`;
}

export function defaultDeliveryAnchorForCadence(
  cadence: BulkCelebrationCadence,
  enabledAt?: Date | null,
): DeliveryAnchor {
  if (cadence === "monthly" || cadence === "quarterly") {
    return { kind: "nth_weekday_of_period", weekday: 5, nth: -1 };
  }
  if (cadence === "weekly" && enabledAt) {
    return { kind: "nth_weekday_of_period", weekday: enabledAt.getDay(), nth: 1 };
  }
  if (cadence === "biweekly") {
    return { kind: "offset_from_period_start", days: 13 };
  }
  return { kind: "nth_weekday_of_period", weekday: 5, nth: -1 };
}

function parseAnchor(raw: unknown): DeliveryAnchor | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const kind = o.kind;
  if (kind === "fixed_day_of_period" && typeof o.day_of_month === "number") {
    return { kind, day_of_month: Math.min(31, Math.max(1, Math.floor(o.day_of_month))) };
  }
  if (
    kind === "nth_weekday_of_period" &&
    typeof o.weekday === "number" &&
    (o.nth === 1 || o.nth === -1)
  ) {
    return { kind, weekday: o.weekday % 7, nth: o.nth };
  }
  if (kind === "first_celebration_in_period") return { kind };
  if (kind === "offset_from_period_start" && typeof o.days === "number") {
    return { kind, days: Math.max(0, Math.floor(o.days)) };
  }
  return null;
}

export function resolveDeliveryAnchor(program: BulkCelebrationProgramRow): DeliveryAnchor {
  return (
    parseAnchor(program.delivery_anchor) ??
    defaultDeliveryAnchorForCadence(
      program.cadence,
      program.enabled_at ? new Date(program.enabled_at) : null,
    )
  );
}

function lastDayOfMonth(y: number, m: number): number {
  return new Date(y, m + 1, 0).getDate();
}

function nthWeekdayInRange(
  periodStart: string,
  periodEnd: string,
  weekday: number,
  nth: 1 | -1,
): string {
  const start = new Date(periodStart + "T12:00:00");
  const end = new Date(periodEnd + "T12:00:00");
  const matches: Date[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    if (cur.getDay() === weekday) matches.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  if (matches.length === 0) return periodEnd;
  return isoDate(nth === -1 ? matches[matches.length - 1]! : matches[0]!);
}

export function resolveDeliveryDate(args: {
  anchor: DeliveryAnchor;
  period_start: string;
  period_end: string;
  celebration_dates?: string[];
}): { delivery_date: string; clamped: boolean; note?: string } {
  const { anchor, period_start, period_end } = args;
  const dates = (args.celebration_dates ?? []).filter(Boolean).sort();

  if (anchor.kind === "first_celebration_in_period" && dates.length > 0) {
    return { delivery_date: dates[0]!, clamped: false };
  }

  if (anchor.kind === "offset_from_period_start") {
    const d = new Date(period_start + "T12:00:00");
    d.setDate(d.getDate() + anchor.days);
    let delivery = isoDate(d);
    let clamped = false;
    if (delivery < period_start) {
      delivery = period_start;
      clamped = true;
    }
    if (delivery > period_end) {
      delivery = period_end;
      clamped = true;
    }
    return {
      delivery_date: delivery,
      clamped,
      note: clamped ? "Delivery date was adjusted to stay within this period." : undefined,
    };
  }

  if (anchor.kind === "fixed_day_of_period") {
    const end = new Date(period_end + "T12:00:00");
    const y = end.getFullYear();
    const m = end.getMonth();
    const dom = Math.min(anchor.day_of_month, lastDayOfMonth(y, m));
    let delivery = isoDate(new Date(y, m, dom));
    let clamped = false;
    if (delivery < period_start) {
      delivery = period_end;
      clamped = true;
    }
    if (delivery > period_end) {
      delivery = period_end;
      clamped = true;
    }
    return {
      delivery_date: delivery,
      clamped,
      note: clamped ? "That day isn't in this period — we used the nearest date in range." : undefined,
    };
  }

  if (anchor.kind === "nth_weekday_of_period") {
    const delivery = nthWeekdayInRange(
      period_start,
      period_end,
      anchor.weekday,
      anchor.nth,
    );
    return { delivery_date: delivery, clamped: false };
  }

  return { delivery_date: period_end, clamped: false };
}

export function normalizePersonName(first: string, last: string): string {
  return `${first}`.trim().toLowerCase() + " " + `${last}`.trim().toLowerCase();
}
